import axios from 'axios';

/**
 * Passkey / WebAuthn ceremony helper (issue #4878).
 *
 * Talks to the laragear/webauthn endpoints (routes/web.php, prefix `webauthn`).
 * Encoding matches the server: incoming challenge/ids are base64url, outgoing
 * credential fields are standard base64 — the exact round-trip laragear expects.
 * Requests go through the app's axios instance, so the XSRF-TOKEN cookie set by
 * GET /sanctum/csrf-cookie authorises these session routes, just like the password
 * login.
 */

function base64UrlToUint8Array(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4;
  const base64 = pad ? padded + '='.repeat(4 - pad) : padded;
  const binary = atob(base64);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function arrayBufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function parseServerOptions(publicKey) {
  publicKey.challenge = base64UrlToUint8Array(publicKey.challenge);

  if (publicKey.user?.id) {
    publicKey.user = { ...publicKey.user, id: base64UrlToUint8Array(publicKey.user.id) };
  }

  ['excludeCredentials', 'allowCredentials']
    .filter((key) => Array.isArray(publicKey[key]))
    .forEach((key) => {
      publicKey[key] = publicKey[key].map((entry) => ({
        ...entry,
        id: base64UrlToUint8Array(entry.id),
      }));
    });

  return publicKey;
}

function parseCredential(credential) {
  const parsed = {
    id: credential.id,
    type: credential.type,
    rawId: arrayBufferToBase64(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {},
  };

  ['clientDataJSON', 'attestationObject', 'authenticatorData', 'signature', 'userHandle']
    .filter((key) => credential.response[key] != null)
    .forEach((key) => {
      parsed.response[key] = arrayBufferToBase64(credential.response[key]);
    });

  return parsed;
}

/**
 * Whether this browser exposes the WebAuthn API at all.
 */
export function browserSupportsWebAuthn() {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

/**
 * Register a new passkey for the currently authenticated user.
 *
 * @param {{ alias?: string }} options
 */
export async function registerPasskey({ alias } = {}) {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true });

  const { data } = await axios.post(route('webauthn.register.options'), {}, { withCredentials: true });
  const publicKey = parseServerOptions(data);
  const credential = await navigator.credentials.create({ publicKey });

  return axios.post(
    route('webauthn.register'),
    { ...parseCredential(credential), alias },
    { withCredentials: true },
  );
}

/**
 * Sign in with a passkey. Establishes the same session cookie as the password login.
 *
 * Email-less by design: the challenge carries no account-specific allowCredentials,
 * so the browser offers the user's discoverable passkeys for this site to pick from —
 * and the endpoint cannot be used to enumerate accounts.
 */
export async function loginWithPasskey() {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true });

  const { data } = await axios.post(route('webauthn.login.options'), {}, { withCredentials: true });
  const publicKey = parseServerOptions(data);
  const credential = await navigator.credentials.get({ publicKey });

  return axios.post(route('webauthn.login'), parseCredential(credential), { withCredentials: true });
}

/**
 * List / rename / revoke the current user's passkeys.
 */
export async function listPasskeys() {
  const { data } = await axios.get(route('webauthn.credentials.index'), { withCredentials: true });
  return data.data;
}

export function renamePasskey(id, alias) {
  return axios.put(route('webauthn.credentials.update', { id }), { alias }, { withCredentials: true });
}

export function deletePasskey(id) {
  return axios.delete(route('webauthn.credentials.destroy', { id }), { withCredentials: true });
}
